import {
  Box,
  Flex,
  HStack,
  Skeleton,
  AlertDescription,
  Alert,
  AlertIcon,
  Link,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { LenderSharesChart } from './LenderSharesChart';
import { FundingRequestEntitiesFragmentGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { FundingRequestsAndInvoicesSnapshotContainer } from 'packages/loans/ui/FundingRequestsAndInvoicesSnapshotContainer';
import { TransactionsFiltersDrawer } from 'packages/transactions/ui/TransactionsFiltersDrawer';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';
import { truncateUuid } from '~/packages/common-frontend/utils';
import {
  LoanStatusBadge,
  StatusBadge,
} from '~/packages/funding-requests/ui/shared/StatusBadge';
import { useGetResolvedLoanByIdQuery } from '~/packages/loans/requests/loans.gql.generated';
import { IdPill } from '~/packages/loans/ui/components/IdPill';
import { FacilityDetails } from '~/packages/loans/ui/FacilityDetails';
import { LoanDetails } from '~/packages/loans/ui/LoanDetails';
import { LoanSettingsMenu } from '~/packages/loans/ui/LoanSettingsMenu';
import { useDeriveTransactionsFiltersFromBackend } from '~/packages/transactions/hooks/transactionsHooks';
import { TimelineStepper } from '~/packages/transactions/ui/TimelineStepper';
import { TransactionsView } from '~/packages/transactions/ui/TransactionsView';

const INITIAL_TANSTACK_TABLE_STATE = {
  pagination: {
    pageSize: 50,
    pageIndex: 0,
  },
};

const ExecutedLoanEditAlert = ({
  destinationLoanId,
}: {
  destinationLoanId: string;
}) => {
  return (
    <Alert status="warning">
      <AlertIcon />
      <AlertDescription w="100%">
        <Flex justifyContent="space-between">
          <Typography fontWeight="medium">
            This loan has been edited. You are not viewing the latest version.
          </Typography>
          <Link as={RouterLink} to={`/loans/${destinationLoanId}`}>
            <Typography
              colorScheme="blue"
              fontWeight="medium"
              variant="base-muted"
            >
              View Latest
            </Typography>
          </Link>
        </Flex>
      </AlertDescription>
    </Alert>
  );
};

const InvoiceDueDateAlert = ({
  nextPaymentDate,
  nextInvoiceId,
}: {
  nextPaymentDate: Util.FullDateStr;
  nextInvoiceId: string;
}) => {
  const daysToNextPayment = commonDate.diffDays(
    nextPaymentDate,
    commonDate.dateToFullDateString(new Date()),
  );
  const isOverdue = daysToNextPayment < 0;

  let message = `An invoice is overdue.`;
  if (daysToNextPayment === 0) {
    message = `An invoice is due today.`;
  } else if (daysToNextPayment > 0) {
    message = `An invoice is due in ${daysToNextPayment} days.`;
  }

  return (
    <Alert status={isOverdue ? 'error' : 'warning'}>
      <AlertIcon />
      <AlertDescription w="100%">
        <Flex justifyContent="space-between">
          <Typography fontWeight="medium">{message}</Typography>
          <Link as={RouterLink} to={`/invoices/${nextInvoiceId}`}>
            <Typography
              colorScheme="blue"
              fontWeight="medium"
              variant="base-muted"
            >
              View Invoice
            </Typography>
          </Link>
        </Flex>
      </AlertDescription>
    </Alert>
  );
};

export const LoanOverview = () => {
  const { loanId } = useParams();
  finleyAssert(loanId, 'loanId is required');
  const { productsLoansEditPermissions } = useFeatureFlags();

  const {
    data: loanData,
    error: loanError,
    loading: loanLoading,
  } = useGetResolvedLoanByIdQuery({
    fetchPolicy: 'no-cache',
    variables: { loanId },
  });
  const loan = loanData?.loan;
  if (loanError) throw loanError;

  // Filters
  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );

  const transactionsFilters = useDeriveTransactionsFiltersFromBackend({
    creditFacilityIds,
    loanIds: [loanId],
  });
  if (transactionsFilters.status === 'error') throw transactionsFilters.error;

  const nextPaymentDate = loan?.invoices?.nextPaymentDate;
  const timelineDisplaySteps = useMemo(() => {
    return [
      {
        id: '1',
        icon: 'sack-dollar',
        title: 'Funding Date',
        description: loan?.dates.originationDate
          ? commonDate.fullDateStrToHumanDateString(loan.dates.originationDate)
          : '-',
      },
      {
        id: '2',
        icon: 'hourglass-start',
        title: 'Next Payment Date',
        description: nextPaymentDate
          ? commonDate.fullDateStrToHumanDateString(nextPaymentDate)
          : '-',
      },
      {
        id: '3',
        icon: 'hourglass-end',
        title: 'Maturity Date',
        description: loan?.dates.maturityDate
          ? commonDate.fullDateStrToHumanDateString(loan.dates.maturityDate)
          : '-',
      },
    ];
  }, [loan?.dates.originationDate, loan?.dates.maturityDate, nextPaymentDate]);

  const loanFundingRequests = useMemo<
    FundingRequestEntitiesFragmentGQLType[]
  >(() => {
    if (!loan) return [];
    return loan.funding.requests
      .slice()
      .sort((a, b) => a.originationDate.localeCompare(b.originationDate));
  }, [loan]);

  const isAnyLoading = transactionsFilters.status === 'pending' || loanLoading;

  return (
    <Box padding={dashboardRoutePadding}>
      <VStack gap="2.5rem" align="start">
        <Flex direction="column" gap="1.5rem" w="100%">
          {loan?.edits.destination?.loanAction.status === 'executed' && (
            <ExecutedLoanEditAlert
              destinationLoanId={loan.edits.destination.loanId}
            />
          )}
          {nextPaymentDate && loan?.invoices?.nextInvoiceId && (
            <InvoiceDueDateAlert
              nextPaymentDate={nextPaymentDate}
              nextInvoiceId={loan.invoices.nextInvoiceId}
            />
          )}
          <HStack justifyContent="space-between">
            {/* // TODO: this is Temporary: @markhaghani, add Jen's updated breadcrumbs that include loan state */}
            {isAnyLoading ? (
              <Skeleton w="50rem" h="3rem" />
            ) : (
              <Breadcrumbs
                breadcrumbs={[
                  { label: 'Funding', path: '/' },
                  { label: 'Loans', path: '/loans' },
                  { label: `${loan?.id}` },
                ]}
              />
            )}
          </HStack>
          <Flex
            alignItems="center"
            gap="1.5rem"
            justifyContent="space-between"
            wrap="wrap"
          >
            <Flex gap="1.5rem">
              {isAnyLoading ? (
                <Skeleton w="50rem" h="3rem" />
              ) : (
                <Typography variant="page-title" trim>
                  {loan?.name || `Loan ${loan?.id && truncateUuid(loan.id)}`}
                </Typography>
              )}
              {loan?.calculatedLoanStatus && !isAnyLoading && (
                <LoanStatusBadge status={loan.calculatedLoanStatus} />
              )}
              {loan?.customerProvided?.status?.name && !isAnyLoading && (
                <StatusBadge
                  icon="circle-info"
                  label={loan.customerProvided.status.name}
                  status="info"
                />
              )}
            </Flex>
            <Flex gap="1rem">
              {loan?.id && <IdPill label="Loan" value={loan.id} />}
              {loan?.facility?.externalFacilityId && (
                <IdPill
                  label="External Facility ID"
                  value={loan.facility.externalFacilityId}
                />
              )}
              {loan?.externalLoanId && (
                <IdPill
                  label="External Loan ID"
                  value={truncateUuid(loan.externalLoanId)}
                />
              )}
              {productsLoansEditPermissions && !!loan && (
                <LoanSettingsMenu loan={loan} />
              )}
            </Flex>
          </Flex>
        </Flex>
        {isAnyLoading ? (
          <Skeleton w="100%" h="6rem" />
        ) : (
          <TimelineStepper
            steps={timelineDisplaySteps}
            completedStepIndex={1}
          />
        )}
        <LenderSharesChart
          lenderSharesData={loan?.funding.lenderShares ?? []}
          totalOutstanding={loan?.funding.totalOutstandingBalance}
          loading={isAnyLoading}
        />
        <FacilityDetails loanLoading={isAnyLoading} loan={loan} />
        <LoanDetails loading={isAnyLoading} loan={loan} showDates={false} />

        {/* *** PAST DRAWS & PAYMENTS *** */}
        {loan && (
          <FundingRequestsAndInvoicesSnapshotContainer
            loan={loan}
            fundingRequests={loanFundingRequests}
            isLoading={isAnyLoading}
          />
        )}

        {/* *** TRANSACTIONS *** */}
        <VStack direction="column" gap="1rem" align="start" w="100%">
          <HStack justifyContent="space-between" w="100%">
            <Typography variant="section-title" trim>
              Transactions
            </Typography>
            {!loan || transactionsFilters.status === 'pending' ? (
              <Skeleton h="50px" />
            ) : (
              <TransactionsFiltersDrawer
                filtersToDisable={{
                  creditFacilityIds: true,
                  loanIds: true,
                }}
                onFilterChange={transactionsFilters.setCurrentFilters}
                {...transactionsFilters.currentFilters}
                loanIds={[loanId]}
              />
            )}
          </HStack>
          <Flex
            sx={{
              w: '100%',
              ...(!isAnyLoading && {
                borderRadius: 'md',
                borderWidth: '1px',
                px: '2rem',
              }),
            }}
          >
            {!loan || transactionsFilters.status === 'pending' ? (
              <Skeleton h="3rem" w="100%" />
            ) : (
              <TransactionsView
                filterDerivedState={{
                  ...transactionsFilters.currentFilters,
                  loanIds: [loanId],
                }}
                initialTanstackTableState={INITIAL_TANSTACK_TABLE_STATE}
              />
            )}
          </Flex>
        </VStack>
      </VStack>
    </Box>
  );
};
